import React from 'react';
import { CheckCircle, Scale, Shield, Target, AlertTriangle, Calendar, FileText, RefreshCw } from 'lucide-react';

// Helper pour formater les dates de manière sécurisée
const formatDateSafe = (dateString) => {
  if (!dateString || dateString === 'null' || dateString === 'undefined') {
    return null;
  }
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return null;
    }
    return date.toLocaleDateString('fr-FR');
  } catch {
    return null;
  }
};

// Helper pour s'assurer qu'une valeur est un tableau
const toArray = (value) => {
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    return [value]; // Convertit une string en tableau d'un élément
  }
  return [];
};

const LV3LegalAnalysis = ({ lv3Data, isAdmin = false, onRetriggerAnalysis, isRetriggering = false }) => {
  if (!lv3Data || Object.keys(lv3Data).length === 0) {
    return null;
  }

  const {
    qualification_juridique_creance,
    analyse_prescription = {},
    actes_interruptifs_identifies = [],
    strategie_defense = {},
    recommandations_actions = [],
    avertissement_informations
  } = lv3Data;

  // Si c'est le message de verrouillage (pas encore payé)
  if (qualification_juridique_creance === "Accessible après paiement") {
    // En mode admin, afficher un message et le bouton pour re-déclencher l'analyse
    if (isAdmin) {
      return (
        <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
          <AlertTriangle size={48} className="mx-auto text-yellow-500 mb-4" />
          <h4 className="text-lg font-semibold text-yellow-800 mb-2">
            Analyse LV3 non générée
          </h4>
          <p className="text-sm text-yellow-700 mb-4">
            Ce dossier a été créé avant l'activation automatique de l'analyse LV3.
            Cliquez sur le bouton ci-dessous pour lancer l'analyse complète.
          </p>
          {onRetriggerAnalysis && (
            <button
              onClick={onRetriggerAnalysis}
              disabled={isRetriggering}
              className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw size={16} className={isRetriggering ? 'animate-spin' : ''} />
              {isRetriggering ? 'Analyse en cours...' : 'Lancer l\'analyse LV3'}
            </button>
          )}
        </div>
      );
    }
    return null;
  }

  return (
    <div className="card p-8 bg-green-50 border-green-200 mb-8">
      <div className="mb-6 text-center">
        <CheckCircle size={48} color="#059669" className="mx-auto mb-4" />
        <h3 className="text-2xl font-bold mb-2 text-green-800">
          ⚖️ Analyse Juridique Complète
        </h3>
        <p className="text-gray-700">
          Votre stratégie d'action personnalisée pour défendre votre dossier
        </p>
      </div>

      {/* Avertissement informations manquantes */}
      {avertissement_informations && (
        <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-6 mb-6">
          <h4 className="text-lg font-bold mb-3 flex items-center text-orange-800">
            <AlertTriangle size={20} className="mr-2" />
            Avertissement important
          </h4>
          <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
            {avertissement_informations}
          </p>
        </div>
      )}

      {/* Qualification juridique */}
      {qualification_juridique_creance && (
        <div className="bg-white rounded-lg p-6 mb-6">
          <h4 className="text-lg font-bold mb-4 flex items-center" style={{ color: '#1e3a8a' }}>
            <Scale size={20} className="mr-2" />
            Qualification juridique de la créance
          </h4>
          <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
            {qualification_juridique_creance}
          </p>
        </div>
      )}

      {/* Analyse prescription/forclusion */}
      {Object.keys(analyse_prescription).length > 0 && (
        <div className="bg-white rounded-lg p-6 mb-6">
          <h4 className="text-lg font-bold mb-4 flex items-center" style={{ color: '#1e3a8a' }}>
            <Calendar size={20} className="mr-2" />
            Analyse de prescription / forclusion
          </h4>
          <div className="space-y-4">
            {analyse_prescription.regime_applicable && (
              <div>
                <p className="text-sm text-gray-500 mb-1">Régime applicable</p>
                <p className="text-base font-semibold text-gray-800">
                  {analyse_prescription.regime_applicable}
                </p>
              </div>
            )}
            {analyse_prescription.point_depart_prescription && (
              <div>
                <p className="text-sm text-gray-500 mb-1">Point de départ de la prescription</p>
                <p className="text-base text-gray-700 leading-relaxed">
                  {analyse_prescription.point_depart_prescription}
                </p>
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-4">
              {formatDateSafe(analyse_prescription.date_expiration_estimee) && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Date d'expiration estimée</p>
                  <p className="text-base font-semibold text-gray-800">
                    {formatDateSafe(analyse_prescription.date_expiration_estimee)}
                  </p>
                </div>
              )}
              {analyse_prescription.niveau_certitude && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Niveau de certitude</p>
                  <p className={`text-base font-semibold ${
                    analyse_prescription.niveau_certitude === 'élevé' ? 'text-green-600' :
                    analyse_prescription.niveau_certitude === 'moyen' ? 'text-yellow-600' :
                    'text-orange-600'
                  }`}>
                    {analyse_prescription.niveau_certitude}
                  </p>
                </div>
              )}
            </div>
            {analyse_prescription.explication_detaillee && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 mt-4">
                <p className="text-sm font-semibold text-blue-800 mb-2">Analyse juridique détaillée</p>
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {analyse_prescription.explication_detaillee}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Actes interruptifs identifiés */}
      {toArray(actes_interruptifs_identifies).length > 0 && (
        <div className="bg-white rounded-lg p-6 mb-6">
          <h4 className="text-lg font-bold mb-4 flex items-center" style={{ color: '#1e3a8a' }}>
            <FileText size={20} className="mr-2" />
            Actes interruptifs identifiés
          </h4>
          <div className="space-y-4">
            {toArray(actes_interruptifs_identifies).map((acte, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="grid md:grid-cols-3 gap-3 mb-3">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Type d'acte</p>
                    <p className="text-sm font-semibold text-gray-800">{acte.type}</p>
                  </div>
                  {formatDateSafe(acte.date) && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Date</p>
                      <p className="text-sm font-semibold text-gray-800">
                        {formatDateSafe(acte.date)}
                      </p>
                    </div>
                  )}
                  {acte.validite && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Validité</p>
                      <p className={`text-sm font-semibold ${
                        acte.validite === 'valide' ? 'text-green-600' :
                        acte.validite === 'invalide' ? 'text-red-600' :
                        'text-yellow-600'
                      }`}>
                        {acte.validite}
                      </p>
                    </div>
                  )}
                </div>
                {acte.impact_prescription && (
                  <div className="mb-2">
                    <p className="text-xs text-gray-500 mb-1">Impact sur la prescription</p>
                    <p className="text-sm text-gray-700">{acte.impact_prescription}</p>
                  </div>
                )}
                {acte.commentaire && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Analyse</p>
                    <p className="text-sm text-gray-700 italic">{acte.commentaire}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stratégie de défense */}
      {Object.keys(strategie_defense).length > 0 && (
        <div className="bg-white rounded-lg p-6 mb-6">
          <h4 className="text-lg font-bold mb-4 flex items-center" style={{ color: '#1e3a8a' }}>
            <Shield size={20} className="mr-2" />
            Stratégie de défense
          </h4>

          {/* Axes principaux */}
          {toArray(strategie_defense.axes_principaux).length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                <Target size={16} className="mr-2" />
                Axes principaux de défense
              </p>
              <ul className="space-y-2">
                {toArray(strategie_defense.axes_principaux).map((axe, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    <span className="text-gray-700">{axe}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Arguments juridiques */}
          {toArray(strategie_defense.arguments_juridiques).length > 0 && (
            <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm font-semibold text-blue-800 mb-2">⚖️ Arguments juridiques</p>
              <ul className="space-y-2">
                {toArray(strategie_defense.arguments_juridiques).map((argument, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span className="text-gray-700">{argument}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Risques et opportunités */}
          {strategie_defense.risques_et_opportunites && (
            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-sm font-semibold text-yellow-800 mb-2">⚖️ Risques et opportunités</p>
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                {strategie_defense.risques_et_opportunites}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Recommandations d'actions */}
      {toArray(recommandations_actions).length > 0 && (
        <div className="bg-white rounded-lg p-6 mb-6">
          <h4 className="text-lg font-bold mb-4 flex items-center" style={{ color: '#1e3a8a' }}>
            <Target size={20} className="mr-2" />
            Recommandations d'actions
          </h4>
          <div className="space-y-3">
            {toArray(recommandations_actions).map((recommandation, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg border-l-4 ${
                  recommandation.priorite === 'haute' ? 'bg-red-50 border-red-500' :
                  recommandation.priorite === 'moyenne' ? 'bg-yellow-50 border-yellow-500' :
                  'bg-blue-50 border-blue-500'
                }`}
              >
                <div className="flex justify-between items-start mb-2">
                  <p className="font-semibold text-gray-800">{recommandation.action}</p>
                  <span className={`text-xs px-2 py-1 rounded ${
                    recommandation.priorite === 'haute' ? 'bg-red-100 text-red-700' :
                    recommandation.priorite === 'moyenne' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-blue-100 text-blue-700'
                  }`}>
                    {recommandation.priorite}
                  </span>
                </div>
                {recommandation.delai && (
                  <p className="text-sm text-gray-600 mb-1">
                    ⏱️ Délai : {recommandation.delai}
                  </p>
                )}
                {recommandation.justification && (
                  <p className="text-sm text-gray-700 italic">
                    {recommandation.justification}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Disclaimer */}
      <div className="mt-6 p-4 bg-gray-100 rounded-lg border border-gray-300">
        <p className="text-xs text-gray-600 italic">
          ⚖️ <strong>Important :</strong> Cette analyse est basée sur les éléments fournis et ne constitue pas une consultation juridique.
          Pour une défense optimale de vos droits, nous vous recommandons de consulter un avocat spécialisé en droit de la prescription
          qui pourra examiner l'intégralité de vos documents originaux et vous conseiller de manière personnalisée.
        </p>
      </div>
    </div>
  );
};

export default LV3LegalAnalysis;
